import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Tabs,
  Tab,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  LinearProgress,
  InputAdornment,
  Tooltip,
  Badge,
  Autocomplete,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Block,
  CheckCircle,
  Business,
  Store,
  Person,
  AttachMoney,
  TrendingUp,
  Settings,
  Key,
  Email,
  Phone,
  LocationOn,
  AccountBalance,
  Warning,
  Info,
  Refresh,
  Download,
  Upload,
  FileCopy,
  Visibility,
  VisibilityOff,
  ArrowForward,
  HelpOutline,
  OpenInNew,
  Send,
  Cancel,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { partnerService, configService } from '../services/api';
import { format } from 'date-fns';
import { countries, Country } from '../data/countries';
import { formatPhoneNumber, validatePhoneNumber, getPhoneNumberError } from '../utils/phoneValidation';
import { getUrlError } from '../utils/urlValidation';
import { getErrorMessage } from '../utils/errorUtils';
import ConfirmDialog, { DialogType } from '../components/ConfirmDialog';
import NotificationSnackbar, { NotificationType } from '../components/NotificationSnackbar';

type RetailerType = 'POST_PAID' | 'PRE_PAID';

interface Partner {
  id: number;
  partnerCode: string;
  partnerName: string;
  partnerType: 'MERCHANT' | 'RETAILER' | 'AGENT';
  retailerType?: RetailerType;
  partnerStatus: 'DRAFT' | 'PENDING_APPROVAL' | 'ACTIVE' | 'SUSPENDED' | 'TERMINATED' | 'PENDING_ONBOARDING';

  // Contact information
  email: string;
  phoneNumber: string;

  // Business address
  businessAddress: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  country?: string;

  // Business details
  businessRegistrationNumber?: string;
  taxIdentificationNumber?: string;

  // Primary contact
  primaryContactName?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string;

  // Merchant-specific
  merchantCategoryCode?: string;
  website?: string;
  businessDescription?: string;
  callbackUrl?: string;

  // Retailer-specific
  storeCode?: string;
  storeAddress?: string;
  preferredCommissionRate?: number;
  numberOfLocations?: number;

  // Agent-specific
  agentCode?: string;
  territory?: string;
  supervisorName?: string;
  supervisorContact?: string;
  initialFloatAmount?: number;

  // Financial settings
  defaultCommissionRate: number;
  specialCommissionRate?: number;
  balance?: number;
  floatBalance?: number;
  settlementFrequency: string;
  settlementDelayDays?: number;
  transactionLimitDaily?: number;
  transactionLimitMonthly?: number;
  riskLevel?: string;

  // API & Security
  apiKey?: string;
  apiKeyGenerated?: boolean;

  // Status & timestamps
  onboardedAt?: string;
  verifiedAt?: string;
  suspendedAt?: string;
  suspensionReason?: string;
  totalTransactions?: number;
  totalVolume?: number;
  createdAt?: string;
  approvedAt?: string;
  approvedBy?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const PartnerManagement: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showApiKey, setShowApiKey] = useState<string | null>(null);
  const [commissionRates, setCommissionRates] = useState<any>({});
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [primaryContactPhoneError, setPrimaryContactPhoneError] = useState<string | null>(null);
  const [callbackUrlError, setCallbackUrlError] = useState<string | null>(null);
  const [backendErrors, setBackendErrors] = useState<Record<string, string>>({});

  // Dialog and notification states
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    type: 'confirm' as DialogType,
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    inputLabel: '',
    inputPlaceholder: '',
    inputRequired: false,
    inputMultiline: false,
    onConfirm: (inputValue?: string) => {},
  });

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    type: 'success' as NotificationType,
  });

  // Helper function to show notification
  const showNotification = (message: string, type: NotificationType = 'success') => {
    setNotification({ open: true, message, type });
  };

  // Helper function to show confirmation dialog
  const showConfirmDialog = (
    title: string,
    message: string,
    onConfirm: (inputValue?: string) => void,
    options: Partial<typeof confirmDialog> = {}
  ) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      type: 'confirm',
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      inputLabel: '',
      inputPlaceholder: '',
      inputRequired: false,
      inputMultiline: false,
      onConfirm,
      ...options,
    });
  };

  // Form state for new/edit partner
  const [formData, setFormData] = useState({
    // Basic info
    name: '',
    type: 'MERCHANT' as Partner['partnerType'],
    email: '',
    phone: '',

    // Business address
    address: '',
    city: '',
    stateProvince: '',
    postalCode: '',
    country: 'ZW',

    // Business details
    businessRegistrationNumber: '',
    taxIdentificationNumber: '',

    // Primary contact
    primaryContactName: '',
    primaryContactEmail: '',
    primaryContactPhone: '',

    // Merchant-specific
    merchantCategoryCode: '',
    website: '',
    businessDescription: '',
    callbackUrl: '',

    // Retailer-specific
    retailerType: 'POST_PAID' as RetailerType,
    storeCode: '',
    storeAddress: '',
    numberOfLocations: 0,

    // Agent-specific
    agentCode: '',
    territory: '',
    supervisorName: '',
    supervisorContact: '',
    initialFloatAmount: 0,

    // Financial
    commissionRate: 0,
    customCommission: false,
    settlementFrequency: 'T+1',
    riskLevel: 'LOW',
  });

  useEffect(() => {
    fetchPartners();
    fetchCommissionRates();
  }, [tabValue]);

  const fetchPartners = async () => {
    setLoading(true);
    try {
      let params: any = {};

      // Filter by partner type based on tab
      if (tabValue === 1) params.partnerType = 'MERCHANT';
      else if (tabValue === 2) params.partnerType = 'RETAILER';
      else if (tabValue === 3) params.partnerType = 'AGENT';

      const response = await partnerService.getPartners(params);
      const partnersData = response.content || response.data || response || [];

      setPartners(partnersData);
    } catch (error) {
      console.error('Failed to fetch partners:', error);
      // Fallback to empty array if API call fails
      setPartners([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommissionRates = async () => {
    try {
      // const rates = await configService.getCommissionRates();
      setCommissionRates({
        default: 2.0,
        merchant: 2.5,
        retailer: 1.5,
        agent: 3.0,
      });
    } catch (error) {
      console.error('Failed to fetch commission rates:', error);
    }
  };

  const handleCreatePartner = () => {
    setEditMode(false);
    setPhoneError(null);
    setPrimaryContactPhoneError(null);
    setCallbackUrlError(null);
    setBackendErrors({});
    setFormData({
      // Basic info
      name: '',
      type: 'MERCHANT',
      email: '',
      phone: '',

      // Business address
      address: '',
      city: '',
      stateProvince: '',
      postalCode: '',
      country: 'ZW',

      // Business details
      businessRegistrationNumber: '',
      taxIdentificationNumber: '',

      // Primary contact
      primaryContactName: '',
      primaryContactEmail: '',
      primaryContactPhone: '',

      // Merchant-specific
      merchantCategoryCode: '',
      website: '',
      businessDescription: '',
      callbackUrl: '',

      // Retailer-specific
      retailerType: 'POST_PAID',
      storeCode: '',
      storeAddress: '',
      numberOfLocations: 0,

      // Agent-specific
      agentCode: '',
      territory: '',
      supervisorName: '',
      supervisorContact: '',
      initialFloatAmount: 0,

      // Financial
      commissionRate: commissionRates['merchant'] || 2.0,
      customCommission: false,
      settlementFrequency: 'T+1',
      riskLevel: 'LOW',
    });
    setDialogOpen(true);
  };

  const handleEditPartner = (partner: Partner) => {
    setEditMode(true);
    setSelectedPartner(partner);
    setPhoneError(null);  // Clear any previous phone errors
    setPrimaryContactPhoneError(null);
    setCallbackUrlError(null);  // Clear any previous callback URL errors
    setBackendErrors({});
    setFormData({
      name: partner.partnerName,
      type: partner.partnerType,
      retailerType: partner.retailerType || 'POST_PAID',  // Use existing or default
      email: partner.email,
      phone: partner.phoneNumber,
      address: partner.businessAddress,
      city: partner.city || '',
      stateProvince: partner.stateProvince || '',
      postalCode: partner.postalCode || '',
      country: partner.country || '',

      // Business details
      businessRegistrationNumber: partner.businessRegistrationNumber || '',
      taxIdentificationNumber: partner.taxIdentificationNumber || '',

      // Primary contact
      primaryContactName: partner.primaryContactName || '',
      primaryContactEmail: partner.primaryContactEmail || '',
      primaryContactPhone: partner.primaryContactPhone || '',

      // Type-specific fields
      merchantCategoryCode: partner.merchantCategoryCode || '',
      website: partner.website || '',
      businessDescription: partner.businessDescription || '',
      callbackUrl: (partner as any).callbackUrl || '',
      storeCode: partner.storeCode || '',
      storeAddress: partner.storeAddress || '',
      numberOfLocations: partner.numberOfLocations || 0,
      agentCode: partner.agentCode || '',
      territory: partner.territory || '',
      supervisorName: partner.supervisorName || '',
      supervisorContact: partner.supervisorContact || '',
      initialFloatAmount: partner.initialFloatAmount || 0,

      // Financial
      commissionRate: partner.defaultCommissionRate,
      customCommission: partner.specialCommissionRate !== undefined && partner.specialCommissionRate !== null,
      settlementFrequency: partner.settlementFrequency,
      riskLevel: partner.riskLevel || 'LOW',
    });
    setDialogOpen(true);
  };

  const handlePhoneChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    setFormData({ ...formData, phone: formatted });

    // Validate phone number
    const error = getPhoneNumberError(formatted);
    setPhoneError(error);
  };

  const handleCallbackUrlChange = (value: string) => {
    setFormData({ ...formData, callbackUrl: value });
    setBackendErrors({ ...backendErrors, callbackUrl: '' });

    // Only validate if there's a value (since it's optional)
    if (value && value.trim() !== '') {
      const error = getUrlError(value, 'Callback URL', false);
      setCallbackUrlError(error);
    } else {
      setCallbackUrlError(null);
    }
  };

  const handlePrimaryContactPhoneChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    setFormData({ ...formData, primaryContactPhone: formatted });
    setBackendErrors({ ...backendErrors, primaryContactPhone: '' });

    // Validate if there's a value
    if (formatted && formatted.trim() !== '') {
      const error = getPhoneNumberError(formatted);
      setPrimaryContactPhoneError(error);
    } else {
      setPrimaryContactPhoneError(null);
    }
  };

  const handleSavePartner = async () => {
    try {
      // Clear previous errors
      setBackendErrors({});

      // Validate main phone before saving
      const phoneValidationError = getPhoneNumberError(formData.phone);
      if (phoneValidationError) {
        setPhoneError(phoneValidationError);
        showNotification('Please fix the phone number error', 'error');
        return;
      }

      // Validate primary contact phone if provided
      if (formData.primaryContactPhone && formData.primaryContactPhone.trim() !== '') {
        const primaryPhoneError = getPhoneNumberError(formData.primaryContactPhone);
        if (primaryPhoneError) {
          setPrimaryContactPhoneError(primaryPhoneError);
          showNotification('Please fix the primary contact phone number error', 'error');
          return;
        }
      }

      // Validate callback URL if provided for merchants
      if (formData.type === 'MERCHANT' && formData.callbackUrl && formData.callbackUrl.trim() !== '') {
        const urlValidationError = getUrlError(formData.callbackUrl, 'Callback URL', false);
        if (urlValidationError) {
          setCallbackUrlError(urlValidationError);
          showNotification(urlValidationError, 'error');
          return;
        }
      }

      if (editMode && selectedPartner) {
        const updateData: any = {
          partnerName: formData.name,
          email: formData.email,
          phoneNumber: formData.phone,
          alternativePhone: formData.primaryContactPhone || undefined,
          businessRegistrationNumber: formData.businessRegistrationNumber || undefined,
          taxIdentificationNumber: formData.taxIdentificationNumber || undefined,
          businessAddress: formData.address,
          city: formData.city,
          stateProvince: formData.stateProvince || undefined,
          postalCode: formData.postalCode || undefined,
          country: formData.country,
          primaryContactName: formData.primaryContactName,
          primaryContactEmail: formData.primaryContactEmail || undefined,
          primaryContactPhone: formData.primaryContactPhone || undefined,
          defaultCommissionRate: formData.commissionRate,
          specialCommissionRate: formData.customCommission ? formData.commissionRate : undefined,
          settlementFrequency: formData.settlementFrequency,
          riskLevel: formData.riskLevel,
        };

        await partnerService.updatePartner(selectedPartner.id.toString(), updateData);
      } else {
        // Base partner data (all partner types)
        const baseData: any = {
          partnerName: formData.name,
          email: formData.email,
          phoneNumber: formData.phone,
          businessAddress: formData.address,
          city: formData.city,
          stateProvince: formData.stateProvince,
          postalCode: formData.postalCode,
          country: formData.country,
          businessRegistrationNumber: formData.businessRegistrationNumber,
          taxIdentificationNumber: formData.taxIdentificationNumber,
          primaryContactName: formData.primaryContactName,
          primaryContactEmail: formData.primaryContactEmail,
          primaryContactPhone: formData.primaryContactPhone,
          riskLevel: formData.riskLevel,
        };

        if (formData.type === 'MERCHANT') {
          const merchantData = {
            ...baseData,
            merchantCategoryCode: formData.merchantCategoryCode,
            website: formData.website,
            businessDescription: formData.businessDescription,
            callbackUrl: formData.callbackUrl || undefined,
            defaultCommissionRate: formData.commissionRate,
            settlementFrequency: formData.settlementFrequency,
          };
          await partnerService.createMerchant(merchantData);
        } else if (formData.type === 'RETAILER') {
          const retailerData = {
            ...baseData,
            retailerType: formData.retailerType,
            storeCode: formData.storeCode,
            storeAddress: formData.storeAddress,
            preferredCommissionRate: formData.commissionRate,
            numberOfLocations: formData.numberOfLocations,
            settlementFrequency: formData.settlementFrequency,
          };
          await partnerService.createRetailer(retailerData);
        } else if (formData.type === 'AGENT') {
          const agentData = {
            ...baseData,
            agentCode: formData.agentCode,
            territory: formData.territory,
            supervisorName: formData.supervisorName,
            supervisorContact: formData.supervisorContact,
            // Agent float is managed in the external float system; no initial float here.
            settlementFrequency: formData.settlementFrequency,
          };
          await partnerService.createAgent(agentData);
        }
      }
      setDialogOpen(false);
      showNotification(`${formData.type === 'MERCHANT' ? 'Merchant' : formData.type === 'RETAILER' ? 'Retailer' : 'Agent'} partner created successfully!`, 'success');
      fetchPartners();
    } catch (error: any) {
      console.error('Failed to save partner:', error);

      // Handle backend validation errors
      if (error.response?.status === 400 && error.response?.data?.fieldErrors) {
        const fieldErrors = error.response.data.fieldErrors;
        setBackendErrors(fieldErrors);

        // Map backend field errors to frontend field errors
        if (fieldErrors.phoneNumber) {
          setPhoneError(fieldErrors.phoneNumber);
        }
        if (fieldErrors.primaryContactPhone) {
          setPrimaryContactPhoneError(fieldErrors.primaryContactPhone);
        }
        if (fieldErrors.callbackUrl) {
          setCallbackUrlError(fieldErrors.callbackUrl);
        }

        showNotification('Please fix the validation errors in the form', 'error');
      } else {
        const errorMessage = getErrorMessage(error, 'Failed to save partner. Please try again.');
        showNotification(errorMessage, 'error');
      }
    }
  };

  const handleSuspendPartner = (partner: Partner) => {
    if (partner.partnerStatus === 'ACTIVE') {
      showConfirmDialog(
        'Suspend Partner',
        `Are you sure you want to suspend ${partner.partnerName}? They will not be able to process transactions until reactivated.`,
        async (reason) => {
          try {
            await partnerService.suspendPartner(partner.id.toString(), reason!);
            showNotification(`Partner ${partner.partnerName} has been suspended.`, 'warning');
            fetchPartners();
          } catch (error) {
            console.error('Failed to suspend partner:', error);
            const errorMessage = getErrorMessage(error, 'Failed to suspend partner. Please try again.');
            showNotification(errorMessage, 'error');
          }
        },
        {
          type: 'input',
          inputLabel: 'Suspension Reason',
          inputPlaceholder: 'Please provide a reason for suspension...',
          inputRequired: true,
          confirmText: 'Suspend',
          cancelText: 'Cancel'
        }
      );
    } else {
      showConfirmDialog(
        'Reactivate Partner',
        `Are you sure you want to reactivate ${partner.partnerName}? They will be able to process transactions again.`,
        async () => {
          try {
            await partnerService.activatePartner(partner.id.toString());
            showNotification(`Partner ${partner.partnerName} has been reactivated.`, 'success');
            fetchPartners();
          } catch (error) {
            console.error('Failed to reactivate partner:', error);
            const errorMessage = getErrorMessage(error, 'Failed to reactivate partner. Please try again.');
            showNotification(errorMessage, 'error');
          }
        },
        {
          type: 'warning',
          confirmText: 'Reactivate'
        }
      );
    }
  };

  const handleGenerateApiKey = (partner: Partner) => {
    const isFirstTimeGeneration = !partner.apiKeyGenerated;
    showConfirmDialog(
      isFirstTimeGeneration ? 'Generate API Key' : 'Generate New API Key',
      isFirstTimeGeneration
        ? `Generate an API key for ${partner.partnerName}? This will allow them to access the API.`
        : `Generate a new API key for ${partner.partnerName}? The old key will be invalidated.`,
      async () => {
        try {
          const response = await partnerService.generateApiKey(partner.id.toString());

          // Update the partner's apiKeyGenerated status locally
          setPartners(prevPartners =>
            prevPartners.map(p =>
              p.id === partner.id
                ? { ...p, apiKeyGenerated: true }
                : p
            )
          );

          showNotification(
            isFirstTimeGeneration
              ? `API key generated successfully for ${partner.partnerName}. You can now send it to ${partner.email}`
              : `New API key generated for ${partner.partnerName}`,
            'success'
          );
        } catch (error) {
          console.error('Failed to generate API key:', error);
          const errorMessage = getErrorMessage(error, 'Failed to generate API key. Please try again.');
          showNotification(errorMessage, 'error');
        }
      },
      {
        type: isFirstTimeGeneration ? 'info' : 'warning',
        confirmText: 'Generate'
      }
    );
  };

  const handleSendApiKey = (partner: Partner) => {
    showConfirmDialog(
      'Send API Key',
      `Send the API key to ${partner.email}?`,
      async () => {
        try {
          // First, get the partner's API keys
          const apiKeys = await partnerService.getApiKeys(partner.id.toString());
          const activeKey = apiKeys.find((key: any) => key.isActive);

          if (!activeKey) {
            showNotification('No active API key found for this partner. Please generate one first.', 'error');
            return;
          }

          await partnerService.sendApiKeyEmail(partner.id.toString(), activeKey.id);
          showNotification(`API key has been sent to ${partner.email}`, 'success');
        } catch (error) {
          console.error('Failed to send API key:', error);
          const errorMessage = getErrorMessage(error, 'Failed to send API key email. Please try again.');
          showNotification(errorMessage, 'error');
        }
      },
      { confirmText: 'Send Email' }
    );
  };

  const handleCompleteOnboarding = (partner: Partner) => {
    showConfirmDialog(
      'Complete Onboarding',
      `Complete onboarding for ${partner.partnerName}? This will activate the partner and they will be able to start processing transactions.`,
      async () => {
        try {
          await partnerService.activatePartner(partner.id.toString());
          showNotification(`Partner ${partner.partnerName} has been activated successfully!`, 'success');
          fetchPartners();
        } catch (error) {
          console.error('Failed to complete onboarding:', error);
          const errorMessage = getErrorMessage(error, 'Failed to complete onboarding. Please try again.');
          showNotification(errorMessage, 'error');
        }
      },
      { type: 'info', confirmText: 'Activate Partner' }
    );
  };

  const handleSubmitForApproval = (partner: Partner) => {
    if (partner.partnerStatus !== 'DRAFT') {
      showNotification('Only partners in DRAFT status can be submitted for approval.', 'warning');
      return;
    }

    showConfirmDialog(
      'Submit for Approval',
      `Submit ${partner.partnerName} for approval? This will move the partner to PENDING_APPROVAL status and notify the approvers.`,
      async () => {
        try {
          await partnerService.submitForApproval(partner.id.toString());
          showNotification(`Partner ${partner.partnerName} has been submitted for approval.`, 'success');
          fetchPartners();
        } catch (error) {
          console.error('Failed to submit partner for approval:', error);
          const errorMessage = getErrorMessage(error, 'Failed to submit partner for approval. Please try again.');
          showNotification(errorMessage, 'error');
        }
      },
      {
        type: 'info',
        confirmText: 'Submit for Approval'
      }
    );
  };

  const handleApprovePartner = (partner: Partner) => {
    if (partner.partnerStatus !== 'PENDING_APPROVAL') {
      showNotification('Only partners in PENDING_APPROVAL status can be approved.', 'warning');
      return;
    }

    showConfirmDialog(
      'Approve Partner',
      `Approve ${partner.partnerName} for onboarding?`,
      async (comment) => {
        try {
          await partnerService.approvePartner(partner.id.toString(), comment || '');
          showNotification(`Partner ${partner.partnerName} has been approved and moved to PENDING_ONBOARDING status.`, 'success');
          fetchPartners();
        } catch (error) {
          console.error('Failed to approve partner:', error);
          const errorMessage = getErrorMessage(error, 'Failed to approve partner. Please try again.');
          showNotification(errorMessage, 'error');
        }
      },
      {
        type: 'input',
        inputLabel: 'Approval Comments (Optional)',
        inputPlaceholder: 'Enter any comments for this approval...',
        confirmText: 'Approve'
      }
    );
  };

  const handleRejectPartner = (partner: Partner) => {
    if (partner.partnerStatus !== 'PENDING_APPROVAL') {
      showNotification('Only partners in PENDING_APPROVAL status can be rejected.', 'warning');
      return;
    }

    showConfirmDialog(
      'Reject Partner Application',
      `Reject ${partner.partnerName}'s application? This will return the partner to DRAFT status for corrections.`,
      async (reason) => {
        try {
          await partnerService.rejectPartner(partner.id.toString(), reason!);
          showNotification(`Partner ${partner.partnerName} has been rejected and returned to DRAFT status.`, 'info');
          fetchPartners();
        } catch (error) {
          console.error('Failed to reject partner:', error);
          const errorMessage = getErrorMessage(error, 'Failed to reject partner. Please try again.');
          showNotification(errorMessage, 'error');
        }
      },
      {
        type: 'input',
        inputLabel: 'Rejection Reason',
        inputPlaceholder: 'Please provide a reason for rejection...',
        inputRequired: true,
        inputMultiline: true,
        confirmText: 'Reject & Return to Draft',
        cancelText: 'Cancel'
      }
    );
  };

  const getPartnerIcon = (type: Partner['partnerType']) => {
    switch (type) {
      case 'MERCHANT': return <Business />;
      case 'RETAILER': return <Store />;
      case 'AGENT': return <Person />;
    }
  };

  const handleTerminatePartner = (partner: Partner) => {
    showConfirmDialog(
      'Terminate Partner',
      `Are you sure you want to PERMANENTLY terminate ${partner.partnerName}? This action cannot be undone and all data will be archived.`,
      async () => {
        try {
          await partnerService.terminatePartner(partner.id.toString());
          showNotification(`Partner ${partner.partnerName} has been terminated.`, 'info');
          fetchPartners();
        } catch (error) {
          console.error('Failed to terminate partner:', error);
          const errorMessage = getErrorMessage(error, 'Failed to terminate partner. Please try again.');
          showNotification(errorMessage, 'error');
        }
      },
      {
        type: 'danger',
        confirmText: 'Terminate Permanently',
        cancelText: 'Cancel'
      }
    );
  };

  const getStatusColor = (status: Partner['partnerStatus']) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'SUSPENDED': return 'warning';
      case 'TERMINATED': return 'error';
      case 'PENDING_ONBOARDING': return 'info';
      case 'PENDING_APPROVAL': return 'info';
      case 'DRAFT': return 'default';
      default: return 'default';
    }
  };

  const formatRetailerType = (type: RetailerType): string => {
    switch (type) {
      case 'POST_PAID':
        return 'Post-paid';
      case 'PRE_PAID':
        return 'Pre-paid';
      default:
        return type;
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Partner Management</Typography>
        <Box display="flex" gap={2}>
          <Tooltip title="View partner status workflow">
            <IconButton color="primary">
              <HelpOutline />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<Download />}
          >
            Export Partners
          </Button>
          <Button
            variant="outlined"
            startIcon={<Upload />}
          >
            Bulk Import
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreatePartner}
          >
            Add Partner
          </Button>
        </Box>
      </Box>

      {/* Partner Status Workflow */}
      <Paper sx={{ p: 2, mb: 3, background: 'linear-gradient(to right, #f5f5f5, #fff)' }}>
        <Typography variant="h6" gutterBottom>
          Partner Status Workflow
        </Typography>
        <Box display="flex" alignItems="center" justifyContent="space-around" sx={{ mt: 2, flexWrap: 'wrap' }}>
          <Box textAlign="center">
            <Chip label="DRAFT" color="default" sx={{ mb: 1 }} />
            <Typography variant="caption" display="block">Initial creation</Typography>
          </Box>
          <ArrowForward color="action" />
          <Box textAlign="center">
            <Chip label="PENDING_APPROVAL" color="info" sx={{ mb: 1 }} />
            <Typography variant="caption" display="block">Awaiting approval</Typography>
          </Box>
          <ArrowForward color="action" />
          <Box textAlign="center">
            <Chip label="PENDING_ONBOARDING" color="info" sx={{ mb: 1 }} />
            <Typography variant="caption" display="block">Setting up API access</Typography>
          </Box>
          <ArrowForward color="action" />
          <Box textAlign="center">
            <Chip label="ACTIVE" color="success" sx={{ mb: 1 }} />
            <Typography variant="caption" display="block">Fully operational</Typography>
          </Box>
          <ArrowForward color="action" />
          <Box textAlign="center">
            <Chip label="SUSPENDED" color="warning" sx={{ mb: 1 }} />
            <Typography variant="caption" display="block">Temporarily disabled</Typography>
          </Box>
          <ArrowForward color="action" />
          <Box textAlign="center">
            <Chip label="TERMINATED" color="error" sx={{ mb: 1 }} />
            <Typography variant="caption" display="block">Permanently disabled</Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Alert severity="info" sx={{ mb: 1 }}>
            <Typography variant="body2">
              <strong>Status Transitions:</strong>
            </Typography>
            <Typography variant="caption" component="div" sx={{ mt: 1 }}>
              • <strong>DRAFT:</strong> Click "Submit for Approval" to send for review<br/>
              • <strong>PENDING_APPROVAL:</strong> Approve to move to onboarding, or Reject to return to DRAFT<br/>
              • <strong>PENDING_ONBOARDING:</strong> Generate and send API key, then complete onboarding<br/>
              • <strong>ACTIVE:</strong> Can be suspended if needed<br/>
              • <strong>SUSPENDED:</strong> Can be reactivated or permanently terminated
            </Typography>
          </Alert>
        </Box>
      </Paper>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Partners
                  </Typography>
                  <Typography variant="h4">
                    {partners.length}
                  </Typography>
                  <Box display="flex" gap={0.5} mt={1}>
                    {partners.filter(p => p.partnerStatus === 'DRAFT').length > 0 && (
                      <Chip size="small" label={`${partners.filter(p => p.partnerStatus === 'DRAFT').length} Draft`} />
                    )}
                    {partners.filter(p => p.partnerStatus === 'PENDING_APPROVAL').length > 0 && (
                      <Chip size="small" color="info" label={`${partners.filter(p => p.partnerStatus === 'PENDING_APPROVAL').length} Pending`} />
                    )}
                  </Box>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <Business />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Active Partners
                  </Typography>
                  <Typography variant="h4">
                    {partners.filter(p => p.partnerStatus === 'ACTIVE').length}
                  </Typography>
                  <Box mt={1}>
                    <Chip
                      size="small"
                      color="info"
                      label={`${partners.filter(p => p.partnerStatus === 'PENDING_ONBOARDING').length} Onboarding`}
                    />
                  </Box>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <CheckCircle />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Inactive Partners
                  </Typography>
                  <Typography variant="h4">
                    {partners.filter(p => p.partnerStatus === 'SUSPENDED' || p.partnerStatus === 'TERMINATED').length}
                  </Typography>
                  <Box display="flex" gap={0.5} mt={1}>
                    {partners.filter(p => p.partnerStatus === 'SUSPENDED').length > 0 && (
                      <Chip size="small" color="warning" label={`${partners.filter(p => p.partnerStatus === 'SUSPENDED').length} Suspended`} />
                    )}
                    {partners.filter(p => p.partnerStatus === 'TERMINATED').length > 0 && (
                      <Chip size="small" color="error" label={`${partners.filter(p => p.partnerStatus === 'TERMINATED').length} Terminated`} />
                    )}
                  </Box>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <Block />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Avg Commission
                  </Typography>
                  <Typography variant="h4">
                    {partners.length > 0
                      ? `${(partners.reduce((sum, p) => sum + p.defaultCommissionRate, 0) / partners.length).toFixed(1)}%`
                      : '0%'
                    }
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <TrendingUp />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          variant="fullWidth"
        >
          <Tab label="All Partners" />
          <Tab label="Merchants" icon={<Badge badgeContent={2} color="primary"><Business /></Badge>} />
          <Tab label="Retailers" icon={<Badge badgeContent={1} color="primary"><Store /></Badge>} />
          <Tab label="Agents" icon={<Badge badgeContent={1} color="primary"><Person /></Badge>} />
        </Tabs>
      </Paper>

      {/* Partners List */}
      {loading ? (
        <LinearProgress />
      ) : (
        <Grid container spacing={3}>
          {partners.map((partner) => (
            <Grid item xs={12} md={6} lg={4} key={partner.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar sx={{ bgcolor: 'primary.light' }}>
                        {getPartnerIcon(partner.partnerType)}
                      </Avatar>
                      <Box>
                        <Typography variant="h6">{partner.partnerName}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {partner.partnerType}
                          {partner.partnerType === 'RETAILER' && partner.retailerType && (
                            <> ({formatRetailerType(partner.retailerType)})</>
                          )}
                        </Typography>
                      </Box>
                    </Box>
                    <Box display="flex" gap={0.5}>
                      <Chip
                        label={partner.partnerStatus}
                        color={getStatusColor(partner.partnerStatus) as any}
                        size="small"
                      />
                      {partner.partnerType === 'RETAILER' && partner.retailerType && (
                        <Chip
                          label={formatRetailerType(partner.retailerType)}
                          variant="outlined"
                          size="small"
                          color={partner.retailerType === 'PRE_PAID' ? 'primary' : 'secondary'}
                        />
                      )}
                    </Box>
                  </Box>

                  <List dense>
                    <ListItem disablePadding>
                      <ListItemAvatar>
                        <Email fontSize="small" color="action" />
                      </ListItemAvatar>
                      <ListItemText
                        primary={partner.email}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemAvatar>
                        <Phone fontSize="small" color="action" />
                      </ListItemAvatar>
                      <ListItemText
                        primary={partner.phoneNumber}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemAvatar>
                        <AttachMoney fontSize="small" color="action" />
                      </ListItemAvatar>
                      <ListItemText
                        primary={`Commission: ${partner.defaultCommissionRate}%`}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                      {(partner.specialCommissionRate !== undefined && partner.specialCommissionRate !== null) && (
                        <Chip label="Custom" size="small" variant="outlined" />
                      )}
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemAvatar>
                        <AccountBalance fontSize="small" color="action" />
                      </ListItemAvatar>
                      <ListItemText
                        primary={`Settlement: ${partner.settlementFrequency}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  </List>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Transactions
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {partner.totalTransactions?.toLocaleString() || '0'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Total Volume
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        ${partner.totalVolume?.toLocaleString() || '0'}
                      </Typography>
                    </Grid>
                  </Grid>

                  {partner.partnerType === 'AGENT' && (
                    <Box mt={2}>
                      <Alert severity="info" icon={<AccountBalance />}>
                        <Typography variant="body2">
                          Agent float is managed in the external float system
                        </Typography>
                      </Alert>
                    </Box>
                  )}
                </CardContent>
                <CardActions sx={{ flexWrap: 'wrap', justifyContent: 'flex-start', gap: 0.5 }}>
                  {/* View Details button - always available */}
                  <Button
                    size="small"
                    startIcon={<OpenInNew />}
                    onClick={() => navigate(`/partners/${partner.id.toString()}`)}
                    color="primary"
                  >
                    View Details
                  </Button>

                  {/* Edit button - always available except for TERMINATED */}
                  {partner.partnerStatus !== 'TERMINATED' && (
                    <Button
                      size="small"
                      startIcon={<Edit />}
                      onClick={() => handleEditPartner(partner)}
                    >
                      Edit
                    </Button>
                  )}

                  {/* DRAFT status - Submit for Approval */}
                  {partner.partnerStatus === 'DRAFT' && (
                    <Button
                      size="small"
                      color="primary"
                      variant="contained"
                      startIcon={<Send />}
                      onClick={() => handleSubmitForApproval(partner)}
                    >
                      Submit for Approval
                    </Button>
                  )}

                  {/* PENDING_APPROVAL status - Approve/Reject buttons */}
                  {partner.partnerStatus === 'PENDING_APPROVAL' && (
                    <>
                      <Button
                        size="small"
                        color="success"
                        startIcon={<CheckCircle />}
                        onClick={() => handleApprovePartner(partner)}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<Block />}
                        onClick={() => handleRejectPartner(partner)}
                      >
                        Reject
                      </Button>
                    </>
                  )}

                  {/* Onboarding completion button */}
                  {partner.partnerStatus === 'PENDING_ONBOARDING' && (
                    <>
                      <Button
                        size="small"
                        color="primary"
                        startIcon={<CheckCircle />}
                        onClick={() => handleCompleteOnboarding(partner)}
                      >
                        Complete Onboarding
                      </Button>
                      {!partner.apiKeyGenerated ? (
                        <Tooltip title="Generate an API key first before sending it to the partner">
                          <Button
                            size="small"
                            startIcon={<Key />}
                            onClick={() => handleGenerateApiKey(partner)}
                            color="secondary"
                            variant="contained"
                          >
                            Generate API Key
                          </Button>
                        </Tooltip>
                      ) : (
                        <Button
                          size="small"
                          startIcon={<Email />}
                          onClick={() => handleSendApiKey(partner)}
                        >
                          Send API Key
                        </Button>
                      )}
                    </>
                  )}

                  {/* API Key management - for ACTIVE partners */}
                  {partner.partnerStatus === 'ACTIVE' && (
                    <>
                      <Button
                        size="small"
                        startIcon={<Key />}
                        onClick={() => handleGenerateApiKey(partner)}
                      >
                        New API Key
                      </Button>
                      <Button
                        size="small"
                        startIcon={<Email />}
                        onClick={() => handleSendApiKey(partner)}
                        disabled={!partner.apiKeyGenerated}
                      >
                        Send API Key
                      </Button>
                    </>
                  )}

                  {/* Suspend button for ACTIVE partners */}
                  {partner.partnerStatus === 'ACTIVE' && (
                    <Button
                      size="small"
                      color="warning"
                      startIcon={<Block />}
                      onClick={() => handleSuspendPartner(partner)}
                    >
                      Suspend
                    </Button>
                  )}
                  {/* For SUSPENDED partners - Reactivate or Terminate */}
                  {partner.partnerStatus === 'SUSPENDED' && (
                    <>
                      <Button
                        size="small"
                        color="success"
                        startIcon={<CheckCircle />}
                        onClick={() => handleSuspendPartner(partner)}
                      >
                        Reactivate
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<Cancel />}
                        onClick={() => handleTerminatePartner(partner)}
                      >
                        Terminate
                      </Button>
                    </>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editMode ? 'Edit Partner' : 'Create New Partner'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Partner Name"
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  setBackendErrors({ ...backendErrors, partnerName: '' });
                }}
                required
                error={!!backendErrors.partnerName}
                helperText={backendErrors.partnerName || 'Required'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Partner Type</InputLabel>
                <Select
                  value={formData.type}
                  label="Partner Type"
                  onChange={(e) => {
                    const newType = e.target.value as Partner['partnerType'];
                    setFormData({
                      ...formData,
                      type: newType,
                      // Reset commission rate based on type
                      commissionRate: commissionRates[newType.toLowerCase()] || formData.commissionRate
                    });
                  }}
                  disabled={editMode}
                >
                  <MenuItem value="MERCHANT">Merchant</MenuItem>
                  <MenuItem value="RETAILER">Retailer</MenuItem>
                  <MenuItem value="AGENT">Agent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {/* Show Retailer Type field only for RETAILER partners */}
            {formData.type === 'RETAILER' && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Retailer Type</InputLabel>
                  <Select
                    value={formData.retailerType}
                    label="Retailer Type"
                    onChange={(e) => setFormData({ ...formData, retailerType: e.target.value as RetailerType })}
                  >
                    <MenuItem value="POST_PAID">Post-paid Retailer</MenuItem>
                    <MenuItem value="PRE_PAID">Pre-paid Agent</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={formData.type === 'RETAILER' ? 12 : 6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value });
                  setBackendErrors({ ...backendErrors, email: '' });
                }}
                required
                error={!!backendErrors.email}
                helperText={backendErrors.email || 'Required - Business email address'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => {
                  handlePhoneChange(e.target.value);
                  setBackendErrors({ ...backendErrors, phoneNumber: '' });
                }}
                error={!!phoneError || !!backendErrors.phoneNumber}
                helperText={phoneError || backendErrors.phoneNumber || 'Required - Format: +263712345678 or 712345678'}
                placeholder="+263712345678"
                required
                inputProps={{
                  maxLength: 16,  // + and up to 15 digits
                }}
              />
            </Grid>

            {/* Primary Contact */}
            <Grid item xs={12}><Divider><Typography variant="caption">Primary Contact Person</Typography></Divider></Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Contact Name"
                value={formData.primaryContactName}
                onChange={(e) => {
                  setFormData({ ...formData, primaryContactName: e.target.value });
                  setBackendErrors({ ...backendErrors, primaryContactName: '' });
                }}
                required
                error={!!backendErrors.primaryContactName}
                helperText={backendErrors.primaryContactName || 'Required'}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Contact Email"
                type="email"
                value={formData.primaryContactEmail}
                onChange={(e) => {
                  setFormData({ ...formData, primaryContactEmail: e.target.value });
                  setBackendErrors({ ...backendErrors, primaryContactEmail: '' });
                }}
                error={!!backendErrors.primaryContactEmail}
                helperText={backendErrors.primaryContactEmail || 'Optional'}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Contact Phone"
                value={formData.primaryContactPhone}
                onChange={(e) => handlePrimaryContactPhoneChange(e.target.value)}
                placeholder="+263712345678"
                error={!!primaryContactPhoneError || !!backendErrors.primaryContactPhone}
                helperText={primaryContactPhoneError || backendErrors.primaryContactPhone || 'Optional - Format: +263712345678'}
                inputProps={{
                  maxLength: 16,
                }}
              />
            </Grid>

            {/* Business Address */}
            <Grid item xs={12}><Divider><Typography variant="caption">Business Address</Typography></Divider></Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                value={formData.address}
                onChange={(e) => {
                  setFormData({ ...formData, address: e.target.value });
                  setBackendErrors({ ...backendErrors, businessAddress: '' });
                }}
                multiline
                rows={2}
                required
                error={!!backendErrors.businessAddress}
                helperText={backendErrors.businessAddress || 'Required'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => {
                  setFormData({ ...formData, city: e.target.value });
                  setBackendErrors({ ...backendErrors, city: '' });
                }}
                required
                error={!!backendErrors.city}
                helperText={backendErrors.city || 'Required'}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State/Province"
                value={formData.stateProvince}
                onChange={(e) => setFormData({ ...formData, stateProvince: e.target.value })}
                helperText="Optional"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Postal Code"
                value={formData.postalCode}
                onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                helperText="Optional"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                fullWidth
                options={countries}
                getOptionLabel={(option) => option.name}
                value={countries.find(c => c.code === formData.country) || null}
                onChange={(e, newValue) => {
                  setFormData({ ...formData, country: newValue?.code || '' });
                  setBackendErrors({ ...backendErrors, country: '' });
                }}
                renderOption={(props, option) => (
                  <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                    <Box component="span" sx={{ mr: 1, fontWeight: 'bold' }}>
                      {option.code}
                    </Box>
                    {option.name}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country"
                    required
                    error={!!backendErrors.country}
                    helperText={backendErrors.country || 'Required'}
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'new-password',
                    }}
                  />
                )}
              />
            </Grid>

            {/* Business Details */}
            <Grid item xs={12}><Divider><Typography variant="caption">Business Details (Optional)</Typography></Divider></Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Business Registration Number"
                value={formData.businessRegistrationNumber}
                onChange={(e) => setFormData({ ...formData, businessRegistrationNumber: e.target.value })}
                helperText="Optional"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tax Identification Number"
                value={formData.taxIdentificationNumber}
                onChange={(e) => setFormData({ ...formData, taxIdentificationNumber: e.target.value })}
                helperText="Optional"
              />
            </Grid>

            {/* Merchant-specific fields */}
            {formData.type === 'MERCHANT' && (
              <>
                <Grid item xs={12}><Divider><Typography variant="caption">Merchant Details</Typography></Divider></Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Merchant Category Code"
                    value={formData.merchantCategoryCode}
                    onChange={(e) => setFormData({ ...formData, merchantCategoryCode: e.target.value })}
                    helperText="Optional - e.g., 5411 for Grocery"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Website"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://www.example.com"
                    helperText="Optional"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Commission Rate (%)"
                    type="number"
                    value={formData.commissionRate}
                    onChange={(e) => setFormData({ ...formData, commissionRate: parseFloat(e.target.value) })}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Callback URL (optional)"
                    value={formData.callbackUrl}
                    onChange={(e) => handleCallbackUrlChange(e.target.value)}
                    placeholder="https://your-merchant-domain.com/webhook/voucher-payment"
                    helperText={callbackUrlError || "URL to receive payment notifications when vouchers are paid by customers. Leave empty to disable callbacks."}
                    error={!!callbackUrlError}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">🔗</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Business Description"
                    value={formData.businessDescription}
                    onChange={(e) => setFormData({ ...formData, businessDescription: e.target.value })}
                    multiline
                    rows={3}
                  />
                </Grid>
              </>
            )}

            {/* Retailer-specific fields */}
            {formData.type === 'RETAILER' && (
              <>
                <Grid item xs={12}><Divider><Typography variant="caption">Retailer Details</Typography></Divider></Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Store Code"
                    value={formData.storeCode}
                    onChange={(e) => setFormData({ ...formData, storeCode: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Number of Locations"
                    type="number"
                    value={formData.numberOfLocations}
                    onChange={(e) => setFormData({ ...formData, numberOfLocations: parseInt(e.target.value) })}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Commission Rate (%)"
                    type="number"
                    value={formData.commissionRate}
                    onChange={(e) => setFormData({ ...formData, commissionRate: parseFloat(e.target.value) })}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Store Address"
                    value={formData.storeAddress}
                    onChange={(e) => setFormData({ ...formData, storeAddress: e.target.value })}
                    multiline
                    rows={2}
                  />
                </Grid>
              </>
            )}

            {/* Agent-specific fields */}
            {formData.type === 'AGENT' && (
              <>
                <Grid item xs={12}><Divider><Typography variant="caption">Agent Details</Typography></Divider></Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Agent Code"
                    value={formData.agentCode}
                    onChange={(e) => setFormData({ ...formData, agentCode: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Territory"
                    value={formData.territory}
                    onChange={(e) => setFormData({ ...formData, territory: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Supervisor Name"
                    value={formData.supervisorName}
                    onChange={(e) => setFormData({ ...formData, supervisorName: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Supervisor Contact"
                    value={formData.supervisorContact}
                    onChange={(e) => setFormData({ ...formData, supervisorContact: e.target.value })}
                  />
                </Grid>
              </>
            )}

            {/* Common financial settings */}
            <Grid item xs={12}><Divider><Typography variant="caption">Financial Settings</Typography></Divider></Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Settlement Frequency</InputLabel>
                <Select
                  value={formData.settlementFrequency}
                  label="Settlement Frequency"
                  onChange={(e) => setFormData({ ...formData, settlementFrequency: e.target.value })}
                >
                  <MenuItem value="INSTANT">Instant</MenuItem>
                  <MenuItem value="T+1">T+1</MenuItem>
                  <MenuItem value="T+7">T+7</MenuItem>
                  <MenuItem value="T+14">T+14</MenuItem>
                  <MenuItem value="T+30">T+30</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Risk Level</InputLabel>
                <Select
                  value={formData.riskLevel}
                  label="Risk Level"
                  onChange={(e) => setFormData({ ...formData, riskLevel: e.target.value })}
                >
                  <MenuItem value="LOW">Low</MenuItem>
                  <MenuItem value="MEDIUM">Medium</MenuItem>
                  <MenuItem value="HIGH">High</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Agent float fields */}
            {formData.type === 'AGENT' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Commission Rate (%)"
                  type="number"
                  value={formData.commissionRate}
                  onChange={(e) => setFormData({ ...formData, commissionRate: parseFloat(e.target.value) })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.customCommission}
                    onChange={(e) => setFormData({ ...formData, customCommission: e.target.checked })}
                  />
                }
                label="Custom Commission Rate"
              />
            </Grid>
            {/* Agent float is managed in the external float system - no initial float is
                collected here and agents get no wallet account in this platform. */}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSavePartner}
            disabled={!!phoneError || !!primaryContactPhoneError || !!callbackUrlError}
          >
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        inputLabel={confirmDialog.inputLabel}
        inputPlaceholder={confirmDialog.inputPlaceholder}
        inputRequired={confirmDialog.inputRequired}
        onConfirm={(inputValue) => {
          confirmDialog.onConfirm(inputValue);
          setConfirmDialog({ ...confirmDialog, open: false });
        }}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
      />

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ ...notification, open: false })}
      />
    </Box>
  );
};

export default PartnerManagement;